import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  tenantAPI,
  type Zone,
  type CreateZoneData,
  type UpdateZoneData
} from '@/services/api/tenants';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';

// Query keys for consistent caching
export const zoneKeys = {
  all: ['zones'] as const,
  lists: () => [...zoneKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...zoneKeys.lists(), { filters }] as const,
  details: () => [...zoneKeys.all, 'detail'] as const,
  detail: (id: string) => [...zoneKeys.details(), id] as const,
  zipCodes: (id: string) => [...zoneKeys.detail(id), 'zipCodes'] as const,
  // Include tenantId in cache key so different tenants have separate cached counts
  currentRecalls: (ids: string[], tenantId?: string) =>
    [...zoneKeys.all, 'currentRecalls', { ids, tenantId }] as const
};

/**
 * Hook to fetch all zones with caching
 * Cache duration: 10 minutes for zone list
 * Only runs when session is established to ensure JWT is available for RPC calls
 */
export function useZones() {
  const { session, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: zoneKeys.lists(),
    queryFn: async () => {
      const zones = await tenantAPI.getAllZonesWithZipCounts();
      // Filter to only active zones for map display
      return zones.filter(zone => zone.status === 'active');
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - zones don't change frequently
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on tab switch
    refetchOnReconnect: true, // Do refetch on network reconnect
    // Only fetch when session is established (ensures JWT is available for RPC call)
    enabled: !!session && !authLoading
  });
}

/**
 * Hook to fetch paginated zones with caching
 * Follows same pattern as useCampaigns and useDealers for consistency
 * Only runs when session is established to ensure JWT is available for RPC calls
 *
 * @param oemFilter - Optional OEM filter: single string or array of strings (undefined = all OEMs)
 */
export function useZonesPaginated(
  page: number = 1,
  limit: number = 50,
  searchTerm?: string,
  statusFilter?: 'all' | 'active' | 'inactive',
  regionFilter?: string,
  oemFilter?: string | string[], // Filter by OEM(s) - single string or array (undefined = all OEMs)
  includeUserNames: boolean = false // Skip expensive user name lookup for faster initial load
) {
  const { session, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: zoneKeys.list({
      page,
      limit,
      searchTerm,
      statusFilter,
      regionFilter,
      oemFilter, // Include in cache key (works with both string and array)
      includeUserNames
    }),
    queryFn: () =>
      tenantAPI.getZonesPaginated(
        page,
        limit,
        searchTerm,
        statusFilter,
        regionFilter,
        oemFilter, // Pass OEM filter (API now handles both string and array)
        includeUserNames
      ),
    staleTime: 10 * 60 * 1000, // 10 minutes - zones change infrequently (same as campaigns)
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on tab switch
    refetchOnMount: false, // Use cached data if available
    retry: 2,
    // Only fetch when session is established (ensures JWT is available for RPC call)
    enabled: !!session && !authLoading
  });
}

/**
 * Hook to fetch zone ZIP codes with caching
 */
export function useZoneZipCodes(zoneId: string) {
  return useQuery({
    queryKey: zoneKeys.zipCodes(zoneId),
    queryFn: () => tenantAPI.getZoneZipCodes(zoneId),
    staleTime: 15 * 60 * 1000, // 15 minutes - ZIP codes change less frequently
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!zoneId
  });
}

/**
 * Hook to fetch current recall counts for multiple zones in batch.
 * Used by the zone list to show "Recalls (Current)" with color-coding.
 *
 * @param zoneIds - Array of zone IDs to fetch counts for
 * @param tenantId - Optional tenant ID to filter recalls (matches map behavior).
 *                   When provided, only counts recalls belonging to the specified tenant.
 *                   When omitted/undefined, counts all recalls in the zones (legacy behavior).
 */
export function useCurrentRecallCounts(zoneIds: string[], tenantId?: string) {
  const { session, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: zoneKeys.currentRecalls(zoneIds, tenantId),
    queryFn: () => tenantAPI.getCurrentRecallCountsForZones(zoneIds, tenantId),
    enabled: zoneIds.length > 0 && !!session && !authLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes - recalls change with syncs
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false
  });
}

/**
 * Hook to refresh denormalized current recall counts for all active zones.
 */
export function useRefreshZoneRecallCounts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => tenantAPI.refreshZoneRecallCounts(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: zoneKeys.lists() });
      queryClient.invalidateQueries({ queryKey: zoneKeys.list({}) });
    }
  });
}

/**
 * Hook for zone mutations (create, update, delete)
 */
export function useZoneMutations() {
  const queryClient = useQueryClient();

  const createZone = useMutation({
    mutationFn: ({ data, userId }: { data: CreateZoneData; userId?: string }) =>
      tenantAPI.createZone(data, userId),
    onSuccess: newZone => {
      // Update the zones list cache
      queryClient.setQueryData(zoneKeys.lists(), (old: Zone[] = []) => [...old, newZone]);
      // Invalidate paginated queries to refresh counts
      queryClient.invalidateQueries({ queryKey: zoneKeys.list({}) });
      // Invalidate all lists to be safe
      queryClient.invalidateQueries({ queryKey: zoneKeys.lists() });
    }
  });

  const updateZone = useMutation({
    mutationFn: ({ id, data, userId }: { id: string; data: UpdateZoneData; userId?: string }) =>
      tenantAPI.updateZone(id, data, userId),
    onSuccess: (updatedZone, { id }) => {
      // Update the zones list cache
      queryClient.setQueryData(zoneKeys.lists(), (old: Zone[] = []) =>
        old.map(zone => (zone.id === id ? { ...zone, ...updatedZone } : zone))
      );
      // Update specific zone cache if it exists
      queryClient.setQueryData(zoneKeys.detail(id), updatedZone);
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: zoneKeys.list({}) });
    }
  });

  const deleteZone = useMutation({
    mutationFn: ({ id, hard, userId }: { id: string; hard?: boolean; userId?: string }) =>
      tenantAPI.deleteZone(id, hard, userId),
    onSuccess: (_, { id }) => {
      // Remove from zones list cache
      queryClient.setQueryData(zoneKeys.lists(), (old: Zone[] = []) =>
        old.filter(zone => zone.id !== id)
      );
      // Remove specific zone cache
      queryClient.removeQueries({ queryKey: zoneKeys.detail(id) });
      // Invalidate paginated queries
      queryClient.invalidateQueries({ queryKey: zoneKeys.list({}) });
    }
  });

  return {
    createZone,
    updateZone,
    deleteZone
  };
}
